import React, { useMemo } from 'react';
import { CheckCircle2, Send } from 'lucide-react';
import { Avatar, Button, Field, Input, SeverityPill } from '@/ui';
import { FIX_ACTION_BY_KEY, type FixActionKey, type FixFinding, type RecipientKind } from './types';
import { formatCurrencyRound, midRangeFromEstimate } from './_format';

interface StepReviewProps {
  findings: FixFinding[];
  selected: Set<string>;
  actions: Map<string, FixActionKey>;
  credits: Map<string, number | undefined>;
  preparedByName: string;
  preparedDate: string;
  propertyAddress: string;
  inspectorName: string;
  inspectorCompany: string | null;
  inspectorLicense: string | null;
  recipientKind: RecipientKind;
  onRecipientKindChange: (kind: RecipientKind) => void;
  listingAgentName: string;
  listingAgentEmail: string;
  onListingAgentNameChange: (v: string) => void;
  onListingAgentEmailChange: (v: string) => void;
  submitting: boolean;
  submitError: string | null;
  onSubmit: () => void;
}

interface ReviewItem {
  id: string;
  sectionTitle: string;
  indexLabel: string;
  title: string;
  severity: FixFinding['severity'];
  action: FixActionKey;
  cost: number | null;
}

export const StepReview: React.FC<StepReviewProps> = ({
  findings, selected, actions, credits,
  preparedByName, preparedDate, propertyAddress,
  inspectorName, inspectorCompany, inspectorLicense,
  recipientKind, onRecipientKindChange,
  listingAgentName, listingAgentEmail, onListingAgentNameChange, onListingAgentEmailChange,
  submitting, submitError, onSubmit,
}) => {
  const items: ReviewItem[] = useMemo(
    () => findings
      .filter(f => selected.has(f.id))
      .map(f => {
        const action = actions.get(f.id) ?? 'repair';
        const def = FIX_ACTION_BY_KEY[action];
        let cost: number | null = null;
        if (def.showCredit) {
          const explicit = credits.get(f.id);
          cost = explicit != null ? explicit : (midRangeFromEstimate(f.estimatedCostLow, f.estimatedCostHigh, f.estimatedCost) ?? null);
        }
        return {
          id: f.id,
          sectionTitle: f.sectionTitle,
          indexLabel: f.indexLabel,
          title: f.title,
          severity: f.severity,
          action,
          cost,
        };
      }),
    [findings, selected, actions, credits],
  );

  const counts = useMemo(() => ({
    repair: items.filter(i => i.action === 'repair').length,
    replace: items.filter(i => i.action === 'replace').length,
    further: items.filter(i => i.action === 'further_inspection').length,
    credit: items.filter(i => i.action === 'credit').length,
  }), [items]);

  const total = items.reduce((s, i) => s + (i.cost ?? 0), 0);
  const ccInspector = recipientKind === 'listing_agent' && counts.further > 0;

  const inspectorFirst = inspectorName.split(/\s+/)[0] || 'inspector';
  const sendToFirst = recipientKind === 'listing_agent'
    ? (listingAgentName.trim() ? listingAgentName.trim().split(/\s+/)[0] : 'agent')
    : inspectorFirst;

  const formValid = recipientKind === 'inspector'
    ? true
    : listingAgentName.trim().length > 0 && /.+@.+\..+/.test(listingAgentEmail);

  return (
    <main className="max-w-[860px] mx-auto px-4 sm:px-8 pb-32 sm:pb-24">
      <header className="mt-6 sm:mt-8 mb-4">
        <div className="text-overline uppercase text-brand">Step 3 of 3</div>
        <h2 className="mt-1.5 font-display text-display-md text-ink leading-tight">Review your fix list</h2>
        <p className="mt-2 text-body-md text-ink-subtle max-w-prose-narrow">
          Take a look before sending. You can send to your inspector or the listing agent.
        </p>
      </header>

      {/* Screenshot-grade summary card. */}
      <article className="mt-6 rounded-xl border border-border-subtle bg-surface-1 shadow-card overflow-hidden">
        <div className="px-6 sm:px-7 pt-6 pb-5 border-b border-border-subtle bg-gradient-to-b from-surface-2/40 to-surface-1">
          <div className="text-overline uppercase text-brand">Fix list · {propertyAddress}</div>
          <h3 className="mt-1.5 font-display text-display-md text-ink leading-tight">Items requested by buyer</h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm text-ink-subtle">
            <span>Prepared by <b className="text-ink font-medium">{preparedByName}</b></span>
            <span className="text-ink-faint">·</span>
            <span>{preparedDate}</span>
            <span className="text-ink-faint">·</span>
            <span>{items.length} {items.length === 1 ? 'item' : 'items'}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 border-b border-border-subtle">
          <Stat label="Repair / replace" value={counts.repair + counts.replace} />
          <Stat label="Further inspect" value={counts.further} />
          <Stat label="Credit" value={counts.credit} />
          <Stat label="Est. credits" value={`$${total.toLocaleString('en-US')}`} />
        </div>

        <div className="hidden sm:grid grid-cols-[70px_minmax(0,1fr)_140px_120px] gap-4 px-7 py-3 bg-surface-2 text-[10.5px] font-bold uppercase tracking-wider text-ink-subtle">
          <div>Item</div>
          <div>Description</div>
          <div>Action</div>
          <div className="text-right">Credit</div>
        </div>

        <ul className="divide-y divide-border-subtle">
          {items.map(i => {
            const def = FIX_ACTION_BY_KEY[i.action];
            return (
              <li
                key={i.id}
                className="grid grid-cols-[1fr_auto] sm:grid-cols-[70px_minmax(0,1fr)_140px_120px] gap-x-4 gap-y-1 px-5 sm:px-7 py-3.5 items-baseline"
              >
                <div className="font-mono tabular-nums text-caption text-ink-faint sm:row-auto sm:col-auto col-start-1 row-start-1">
                  {i.indexLabel}
                </div>
                <div className="col-start-1 sm:col-auto row-start-2 sm:row-auto">
                  <div className="text-body-md text-ink font-medium leading-snug">{i.title}</div>
                  <div className="mt-0.5 text-caption text-ink-subtle inline-flex items-center gap-1.5 flex-wrap">
                    <SeverityPill severity={i.severity} compact hideDot />
                    <span className="text-ink-faint">·</span>
                    <span>{i.sectionTitle}</span>
                  </div>
                </div>
                <div className="col-start-2 row-start-1 sm:row-auto sm:col-auto inline-flex items-center gap-1.5 text-body-sm text-ink font-medium self-start sm:self-baseline">
                  <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${def.dotClass}`} />
                  {def.label}
                </div>
                <div
                  className={
                    'col-start-2 row-start-2 sm:row-auto sm:col-auto text-right font-mono tabular-nums text-body-sm font-medium ' +
                    (i.cost == null ? 'text-ink-faint' : 'text-ink')
                  }
                >
                  {i.cost == null ? '—' : `$${i.cost.toLocaleString('en-US')}`}
                </div>
              </li>
            );
          })}
        </ul>

        <div className="px-6 sm:px-7 py-4 border-t-2 border-ink-faint/40 flex items-baseline justify-between gap-4">
          <div className="font-display text-heading-md text-ink">Total credits requested</div>
          <div className="font-mono tabular-nums text-display-sm font-semibold text-ink">
            {formatCurrencyRound(total)}
          </div>
        </div>
      </article>

      {/* Recipient picker. Inspector tab is read-only — we already know them.
          Listing-agent tab reveals a name + email form (we don't store this). */}
      <section className="mt-6 rounded-lg border border-border-subtle bg-surface-1 shadow-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-5 pt-3.5">
          <div className="text-overline uppercase text-ink-subtle">Send to</div>
          <div role="tablist" aria-label="Recipient" className="inline-flex p-0.5 bg-surface-2 border border-border-subtle rounded-full">
            <RecipientTab
              active={recipientKind === 'inspector'}
              onClick={() => onRecipientKindChange('inspector')}
            >
              Your inspector
            </RecipientTab>
            <RecipientTab
              active={recipientKind === 'listing_agent'}
              onClick={() => onRecipientKindChange('listing_agent')}
            >
              Listing agent
            </RecipientTab>
          </div>
        </div>

        {recipientKind === 'inspector' ? (
          <div className="flex items-center gap-3.5 px-4 sm:px-5 py-3.5 sm:py-4">
            <Avatar size="md" name={inspectorName} />
            <div className="min-w-0 flex-1">
              <div className="text-body-md text-ink font-semibold truncate">{inspectorName}</div>
              <div className="text-caption text-ink-subtle truncate">
                {[inspectorCompany, inspectorLicense ? `Lic. ${inspectorLicense}` : null].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
          </div>
        ) : (
          <div className="px-4 sm:px-5 py-3.5 sm:py-4">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr] gap-3">
              <Field label="Name">
                <Input
                  value={listingAgentName}
                  onChange={e => onListingAgentNameChange(e.target.value)}
                  placeholder="e.g. Marcus Whitfield"
                  autoComplete="name"
                />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  value={listingAgentEmail}
                  onChange={e => onListingAgentEmailChange(e.target.value)}
                  placeholder="agent@brokerage.com"
                  autoComplete="email"
                />
              </Field>
            </div>
            {ccInspector && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-soft border border-brand/15 text-caption text-brand-ink">
                <CheckCircle2 className="h-3.5 w-3.5 text-brand shrink-0" aria-hidden />
                <span className="text-ink-muted">
                  <b className="text-brand font-semibold">{inspectorFirst}</b> will be CC'd because your list includes a further-inspection item.
                </span>
              </div>
            )}
          </div>
        )}
      </section>

      {submitError && (
        <div className="mt-4 rounded-md border border-sev-critical/40 bg-sev-critical-soft px-4 py-3 text-body-sm text-sev-critical">
          {submitError}
        </div>
      )}

      {/* Desktop send button. Mobile uses the sticky bottom bar managed by
          the page. Copy / Download are handled by the top-bar primary on
          desktop, so a duplicate row here would be noise. */}
      <div className="hidden sm:flex mt-6">
        <Button
          variant="primary"
          size="lg"
          block
          onClick={onSubmit}
          loading={submitting}
          disabled={items.length === 0 || !formValid}
          leadingIcon={<Send />}
        >
          Send to {sendToFirst}
        </Button>
      </div>
    </main>
  );
};

const Stat: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <div className="px-5 py-4 border-r last:border-r-0 border-border-subtle">
    <div className="font-display text-display-sm text-ink leading-none tabular-nums">{value}</div>
    <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">{label}</div>
  </div>
);

const RecipientTab: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={
      'px-3.5 py-1 rounded-full text-caption font-medium transition-colors duration-180 ' +
      (active
        ? 'bg-surface-1 text-ink shadow-xs'
        : 'bg-transparent text-ink-subtle hover:text-ink')
    }
  >
    {children}
  </button>
);
